import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, X, Loader2 } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  fetchBrands,
  fetchLeafCategories,
  fetchMegaMenu,
  fetchProducts,
} from "../lib/api";

const PER_PAGE = 48;

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const menu = params.get("menu") || "";
  const leaf = params.get("leaf") || "";
  const q = params.get("q") || "";
  const brandParam = params.get("brand") || "";
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [selectedBrand, setSelectedBrand] = useState(brandParam);

  useEffect(() => {
    setPage(1);
    setSelectedBrand(brandParam);
  }, [menu, leaf, q, brandParam]);

  const { data: megaMenu = [] } = useQuery({
    queryKey: ["mega-menu"],
    queryFn: fetchMegaMenu,
    staleTime: 5 * 60 * 1000,
  });

  const menuSections = useMemo(
    () => megaMenu.filter((s) => s.type === "categories" || s.type === "popular"),
    [megaMenu]
  );

  const allMenuItems = useMemo(
    () => menuSections.flatMap((s) => s.items.filter((i) => !i.is_link_only)),
    [menuSections]
  );

  const activeMenu = allMenuItems.find((i) => i.slug === menu);

  const { data: leaves = [] } = useQuery({
    queryKey: ["leaves", menu],
    queryFn: () => fetchLeafCategories(menu),
    enabled: Boolean(menu),
    staleTime: 5 * 60 * 1000,
  });

  const activeLeaf = leaves.find((i) => i.slug === leaf);

  const { data: brands = [] } = useQuery({
    queryKey: ["brands", menu, leaf, q],
    queryFn: () => fetchBrands({ menu: menu || undefined, leaf: leaf || undefined, q: q || undefined }),
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: catalog,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["products", menu, leaf, q, selectedBrand, sort, page],
    queryFn: () =>
      fetchProducts({
        menu: menu || undefined,
        leaf: leaf || undefined,
        q: q || undefined,
        brand: selectedBrand || undefined,
        sort,
        page,
        limit: PER_PAGE,
      }),
    placeholderData: keepPreviousData,
  });

  const total = catalog?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageItems = catalog?.items ?? [];

  const setParam = (key, value) => {
    setPage(1);
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "brand") next.delete("brand");
    setParams(next);
  };

  const clearFilters = () => {
    setPage(1);
    setSelectedBrand("");
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    setParams(next);
  };

  const FilterPanel = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-slate-900 mb-3">Categories</h3>
        <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
          <button
            type="button"
            onClick={() => clearFilters()}
            className={`block text-sm w-full text-left px-2 py-1.5 rounded ${
              !menu ? "bg-rp-green text-white" : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            All products
          </button>
          {menuSections.map((section) => (
            <div key={section.slug} className="pt-2">
              <p className="px-2 text-[11px] font-bold uppercase text-rp-green tracking-wide">
                {section.name}
              </p>
              {section.items
                .filter((i) => !i.is_link_only)
                .map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => {
                      setPage(1);
                      const next = new URLSearchParams(params);
                      next.set("menu", item.slug);
                      next.delete("leaf");
                      next.delete("brand");
                      setParams(next);
                    }}
                    className={`block text-sm w-full text-left px-2 py-1.5 rounded ${
                      menu === item.slug
                        ? "bg-rp-green text-white"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    {item.name}
                    {item.product_count > 0 && (
                      <span className="opacity-70 ml-1">({item.product_count})</span>
                    )}
                  </button>
                ))}
            </div>
          ))}
        </div>
      </div>

      {menu && leaves.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Subcategories</h3>
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            <button
              type="button"
              onClick={() => setParam("leaf", "")}
              className={`block text-sm w-full text-left px-2 py-1.5 rounded ${
                !leaf ? "bg-rp-dark text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              All in {activeMenu?.name || menu}
            </button>
            {leaves.map((l) => (
              <button
                key={l.slug}
                type="button"
                onClick={() => setParam("leaf", l.slug)}
                className={`block text-sm w-full text-left px-2 py-1.5 rounded ${
                  leaf === l.slug
                    ? "bg-rp-dark text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {l.name}
                <span className="opacity-70 ml-1">({l.product_count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {brands.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Brands</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setSelectedBrand("");
                const next = new URLSearchParams(params);
                next.delete("brand");
                setParams(next);
              }}
              className={`block text-sm w-full text-left px-2 py-1.5 rounded ${
                !selectedBrand ? "bg-slate-200 font-medium" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              All brands
            </button>
            {brands.map((b) => (
              <label key={b} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedBrand === b}
                  onCheckedChange={() => {
                    setPage(1);
                    setSelectedBrand(selectedBrand === b ? "" : b);
                    const next = new URLSearchParams(params);
                    if (selectedBrand === b) next.delete("brand");
                    else next.set("brand", b);
                    setParams(next);
                  }}
                />
                <span className="text-slate-700">{b}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const title = activeLeaf?.name || activeMenu?.name || (q ? `Results for "${q}"` : "All Parts");

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
          <Link to="/" className="hover:text-rp-green">Home</Link>
          <span>/</span>
          <Link to="/boutique" className="hover:text-rp-green">Shop</Link>
          {activeMenu && (
            <>
              <span>/</span>
              <Link to={`/boutique?menu=${menu}`} className="hover:text-rp-green">
                {activeMenu.name}
              </Link>
            </>
          )}
          {activeLeaf && (
            <>
              <span>/</span>
              <span className="text-slate-800">{activeLeaf.name}</span>
            </>
          )}
        </div>
        <h1 className="mt-2 text-2xl md:text-3xl font-bold text-slate-900">{title}</h1>
        <p className="text-muted-foreground mt-1">
          {isLoading ? "Loading…" : `${total.toLocaleString()} parts found`}
        </p>
      </div>

      {selectedBrand && (
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => {
              setSelectedBrand("");
              const next = new URLSearchParams(params);
              next.delete("brand");
              setParams(next);
            }}
            className="inline-flex items-center gap-1 text-xs bg-rp-green/10 text-rp-green px-3 py-1.5 rounded-full"
          >
            {selectedBrand} <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="flex gap-8">
        <aside className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-44 bg-white border border-border rounded-xl p-5">
            <FilterPanel />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4 gap-3">
            <details className="lg:hidden">
              <summary className="list-none">
                <Button variant="outline" className="gap-2 rounded-full">
                  <SlidersHorizontal className="h-4 w-4" /> Filters
                </Button>
              </summary>
              <div className="mt-4 bg-white border border-border rounded-xl p-5">
                <FilterPanel />
              </div>
            </details>
            <div className="ml-auto w-48">
              <Select value={sort} onValueChange={(v) => { setPage(1); setSort(v); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-rp-green" />
              Loading products…
            </div>
          )}

          {isError && (
            <div className="text-center py-20 text-red-600">
              Failed to load catalog. {error?.message || "Check that the API is running."}
            </div>
          )}

          {!isLoading && !isError && pageItems.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              No parts found. Try adjusting your filters or search term.
            </div>
          )}

          {!isLoading && pageItems.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {pageItems.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1 || isLoading}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-3">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
